import React, { useEffect, useRef, useState } from 'react';
import './AnimatedBackground.css';

const AnimatedBackground = () => {
  const containerRef = useRef(null);
  const audioRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [shapes, setShapes] = useState([]);
  const [isMouseInside, setIsMouseInside] = useState(false);
  const [nextShapeId, setNextShapeId] = useState(0);
  const [laserShots, setLaserShots] = useState([]);
  const [burnMarks, setBurnMarks] = useState([]);
  const [nextLaserId, setNextLaserId] = useState(0);
  const [totalShotsFired, setTotalShotsFired] = useState(0);
  const [shapesDestroyed, setShapesDestroyed] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Create laser sound using Web Audio API
  const createLaserSound = () => {
    if (!soundEnabled) return;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create multiple oscillators for a rich laser sound
      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const oscillator3 = audioContext.createOscillator();
      
      // Create gain nodes for volume control
      const gainNode1 = audioContext.createGain();
      const gainNode2 = audioContext.createGain();
      const gainNode3 = audioContext.createGain();
      const masterGain = audioContext.createGain();
      
      // Connect the audio graph
      oscillator1.connect(gainNode1);
      oscillator2.connect(gainNode2);
      oscillator3.connect(gainNode3);
      gainNode1.connect(masterGain);
      gainNode2.connect(masterGain);
      gainNode3.connect(masterGain);
      masterGain.connect(audioContext.destination);
      
      // Set frequencies for a laser-like sound
      oscillator1.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator2.frequency.setValueAtTime(1200, audioContext.currentTime);
      oscillator3.frequency.setValueAtTime(600, audioContext.currentTime);
      
      // Set waveforms
      oscillator1.type = 'sawtooth';
      oscillator2.type = 'square';
      oscillator3.type = 'triangle';
      
      // Create frequency sweep (laser zap effect) - twice as fast
      oscillator1.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.15);
      oscillator2.frequency.exponentialRampToValueAtTime(300, audioContext.currentTime + 0.15);
      oscillator3.frequency.exponentialRampToValueAtTime(150, audioContext.currentTime + 0.15);
      
      // Set volume envelopes - twice as fast
      const now = audioContext.currentTime;
      masterGain.gain.setValueAtTime(0, now);
      masterGain.gain.linearRampToValueAtTime(0.1, now + 0.005); // Quick attack
      masterGain.gain.exponentialRampToValueAtTime(0.05, now + 0.05);
      masterGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      
      gainNode1.gain.setValueAtTime(0.4, now);
      gainNode2.gain.setValueAtTime(0.3, now);
      gainNode3.gain.setValueAtTime(0.3, now);
      
      // Start and stop the oscillators
      oscillator1.start(now);
      oscillator2.start(now);
      oscillator3.start(now);
      
      oscillator1.stop(now + 0.15); // Half the duration (was 0.3)
      oscillator2.stop(now + 0.15);
      oscillator3.stop(now + 0.15);
      
      console.log('Laser sound played');
    } catch (error) {
      console.log('Audio not supported or blocked:', error);
    }
  };

  // Toggle sound function
  const toggleSound = () => {
    setSoundEnabled(prev => !prev);
    console.log('Sound', soundEnabled ? 'disabled' : 'enabled');
  };

  // Generate a single random shape
  const generateShape = (id) => {
    const shapeTypes = ['triangle', 'square', 'circle', 'diamond', 'hexagon'];
    const x = Math.random() * 80 + 10;
    const y = Math.random() * 80 + 10;
    
    return {
      id: id,
      type: shapeTypes[Math.floor(Math.random() * shapeTypes.length)],
      x: x,
      y: y,
      originalX: x,
      originalY: y,
      size: Math.random() * 35 + 25,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 2,
      opacity: Math.random() * 0.4 + 0.3,
    };
  };

  // Generate initial random shapes
  useEffect(() => {
    const generateInitialShapes = () => {
      const newShapes = [];
      for (let i = 0; i < 12; i++) {
        newShapes.push(generateShape(i));
      }
      setShapes(newShapes);
      setNextShapeId(12);
      console.log('Generated initial shapes:', newShapes.length);
    };

    generateInitialShapes();
  }, []);

  // Add more shapes function
  const addMoreShapes = () => {
    const newShapes = [];
    for (let i = 0; i < 5; i++) {
      newShapes.push(generateShape(nextShapeId + i));
    }
    setShapes(prevShapes => [...prevShapes, ...newShapes]);
    setNextShapeId(prev => prev + 5);
    console.log('Added 5 more shapes. Total:', shapes.length + 5);
  };

  // Remove shape function
  const removeShape = (shapeId) => {
    setShapes(prevShapes => prevShapes.filter(shape => shape.id !== shapeId));
    console.log('Removed shape:', shapeId);
  };

  // Check if laser hit any shapes - delayed hit detection
  const checkDelayedLaserHits = (laserX, laserY) => {
    const hitRadius = 8; // Much smaller hit radius for extreme precision (was 20)
    let closestShape = null;
    let closestDistance = Infinity;
    
    // Find the closest shape within hit radius at the current moment (1 second after click)
    shapes.forEach(shape => {
      const dx = laserX - shape.x;
      const dy = laserY - shape.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance <= hitRadius && distance < closestDistance) {
        closestDistance = distance;
        closestShape = shape;
      }
    });
    
    if (closestShape) {
      console.log(`Delayed laser hit shape ${closestShape.id} at distance ${closestDistance.toFixed(1)} after 1/3 second`);
      
      // Remove only the closest hit shape
      setShapes(prevShapes => prevShapes.filter(shape => shape.id !== closestShape.id));
      setShapesDestroyed(prev => prev + 1);
      
      // Create burn effect at the LASER location
      const burnId = `shape-burn-${closestShape.id}`;
      const newBurn = {
        id: burnId,
        x: laserX, // Use laser click position
        y: laserY, // Use laser click position
        startTime: Date.now(),
        intensity: Math.random() * 0.3 + 0.7, // Higher intensity for shape burns
        isShapeBurn: true,
      };
      setBurnMarks(prev => [...prev, newBurn]);
      
      // Remove shape burn mark after 667ms (1/3 second longer)
      setTimeout(() => {
        setBurnMarks(prev => prev.filter(burn => burn.id !== burnId));
      }, 667);
      
      return true; // Laser hit something
    }
    
    return false; // Laser missed
  };

  // Create laser shot animation
  const createLaserShot = (x, y) => {
    const laserId = nextLaserId;
    setNextLaserId(prev => prev + 1);
    setTotalShotsFired(prev => prev + 1); // Increment total shots counter
    
    // Play laser sound
    createLaserSound();
    
    const newLaser = {
      id: laserId,
      x: x,
      y: y,
      startTime: Date.now(),
    };
    
    setLaserShots(prev => [...prev, newLaser]);
    
    // Check for hits after 1/3 second delay - where shapes actually are at that moment
    setTimeout(() => {
      const hitSomething = checkDelayedLaserHits(x, y);
      
      // Only create background burn if no shapes were hit
      if (!hitSomething) {
        const burnId = `background-burn-${laserId}`;
        const newBurn = {
          id: burnId,
          x: x,
          y: y,
          startTime: Date.now(),
          intensity: Math.random() * 0.3 + 0.2, // Lower intensity for background burns
          isShapeBurn: false,
        };
        setBurnMarks(prev => [...prev, newBurn]);
        
        // Remove background burn mark after 500ms (1/3 second faster)
        setTimeout(() => {
          setBurnMarks(prev => prev.filter(burn => burn.id !== burnId));
        }, 500);
      }
    }, 333); // 1/3 second delay for hit detection (was 1000ms)
    
    // Remove laser after animation completes
    setTimeout(() => {
      setLaserShots(prev => prev.filter(laser => laser.id !== laserId));
    }, 400);
    
    console.log('Laser shot at:', x.toFixed(1), y.toFixed(1), '- checking hit in 1 second...');
  };

  // Handle background click
  const handleBackgroundClick = (e) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const clickX = ((e.clientX - rect.left) / rect.width) * 100;
      const clickY = ((e.clientY - rect.top) / rect.height) * 100;
      
      // Only create laser if not clicking on a shape or button
      if (e.target === containerRef.current || e.target.classList.contains('background-overlay')) {
        createLaserShot(clickX, clickY);
      }
    }
  };

  // Handle mouse movement
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newMouseX = ((e.clientX - rect.left) / rect.width) * 100;
        const newMouseY = ((e.clientY - rect.top) / rect.height) * 100;
        
        setMousePosition({ x: newMouseX, y: newMouseY });
      }
    };

    const handleMouseEnter = () => {
      setIsMouseInside(true);
      console.log('Mouse entered container');
    };

    const handleMouseLeave = () => {
      setIsMouseInside(false);
      console.log('Mouse left container');
      // Reset shapes to original positions when mouse leaves
      setShapes(prevShapes =>
        prevShapes.map(shape => ({
          ...shape,
          x: shape.originalX,
          y: shape.originalY,
        }))
      );
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);
      container.addEventListener('click', handleBackgroundClick);
      
      return () => {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
        container.removeEventListener('click', handleBackgroundClick);
      };
    }
  }, [nextLaserId]);

  // Update shape positions based on mouse position
  useEffect(() => {
    if (!isMouseInside) return;

    const repelDistance = 20; // Distance within which shapes get repelled
    const repelStrength = 15; // How far shapes move away

    setShapes(prevShapes =>
      prevShapes.map(shape => {
        const dx = mousePosition.x - shape.originalX;
        const dy = mousePosition.y - shape.originalY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < repelDistance && distance > 0) {
          // Calculate repel direction (opposite to mouse direction)
          const angle = Math.atan2(dy, dx);
          const repelX = shape.originalX - Math.cos(angle) * repelStrength;
          const repelY = shape.originalY - Math.sin(angle) * repelStrength;

          // Keep shapes within bounds
          const newX = Math.max(5, Math.min(95, repelX));
          const newY = Math.max(5, Math.min(95, repelY));

          return {
            ...shape,
            x: newX,
            y: newY,
          };
        } else {
          // Return to original position when mouse is far
          return {
            ...shape,
            x: shape.originalX,
            y: shape.originalY,
          };
        }
      })
    );
  }, [mousePosition, isMouseInside]);

  // Continuous rotation animation
  useEffect(() => {
    const interval = setInterval(() => {
      setShapes(prevShapes =>
        prevShapes.map(shape => ({
          ...shape,
          rotation: shape.rotation + shape.rotationSpeed,
        }))
      );
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const getShapeStyle = (shape) => {
    const dx = mousePosition.x - shape.x;
    const dy = mousePosition.y - shape.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Visual effects based on distance from mouse
    const maxEffectDistance = 25;
    const influence = Math.max(0, (maxEffectDistance - distance) / maxEffectDistance);
    
    const scale = 1 + influence * 0.8;
    const brightness = 1 + influence * 1.2;
    const glow = influence * 20;

    return {
      left: `${shape.x}%`,
      top: `${shape.y}%`,
      width: `${shape.size}px`,
      height: `${shape.size}px`,
      transform: `translate(-50%, -50%) rotate(${shape.rotation}deg) scale(${scale})`,
      opacity: shape.opacity + influence * 0.4,
      filter: `brightness(${brightness}) drop-shadow(0 0 ${glow}px rgba(255, 215, 0, 0.9))`,
      transition: 'all 0.2s ease-out',
      cursor: 'pointer',
      '--rotation': `${shape.rotation}deg`,
    };
  };

  const handleShapeClick = (e, shapeId) => {
    e.stopPropagation();
    removeShape(shapeId);
  };

  const renderShape = (shape) => {
    const baseClass = `shape shape-${shape.type}`;
    const isGold = shape.id % 2 === 0;
    const colorClass = isGold ? 'shape-gold' : 'shape-black';

    return (
      <div
        key={shape.id}
        className={`${baseClass} ${colorClass} clickable-shape`}
        style={getShapeStyle(shape)}
        onClick={(e) => handleShapeClick(e, shape.id)}
        title={`Click to remove shape ${shape.id}`}
      />
    );
  };

  const renderLaserShot = (laser) => {
    return (
      <div
        key={`laser-${laser.id}`}
        className="laser-shot"
        style={{
          left: `${laser.x}%`,
          top: `${laser.y}%`,
        }}
      />
    );
  };

  const renderBurnMark = (burn) => {
    const maxDuration = burn.isShapeBurn ? 2000 : 1500; // Shape burns last longer
    const age = (Date.now() - burn.startTime) / maxDuration;
    const fadeOut = 1 - age;
    
    return (
      <div
        key={burn.id}
        className={`burn-mark ${burn.isShapeBurn ? 'shape-burn' : 'background-burn'}`}
        style={{
          left: `${burn.x}%`,
          top: `${burn.y}%`,
          opacity: fadeOut * burn.intensity,
          transform: `translate(-50%, -50%) scale(${1 + age * 0.5})`,
        }}
      />
    );
  };

  return (
    <div ref={containerRef} className="animated-background">
      {shapes.map(renderShape)}
      {laserShots.map(renderLaserShot)}
      {burnMarks.map(renderBurnMark)}
      <div className="background-overlay" />
      
      {/* Control Panel */}
      <div className="control-panel">
        <button 
          className="add-shapes-btn"
          onClick={addMoreShapes}
          title="Add 5 more random shapes"
        >
          + Add Shapes
        </button>
        <button 
          className={`sound-toggle-btn ${soundEnabled ? 'sound-on' : 'sound-off'}`}
          onClick={toggleSound}
          title={soundEnabled ? "Disable laser sounds" : "Enable laser sounds"}
        >
          {soundEnabled ? '🔊' : '🔇'} Sound
        </button>
        <div className="shape-counter">
          Shapes: {shapes.length}
        </div>
        <div className="laser-counter">
          Total Shots: {totalShotsFired}
        </div>
        <div className="burn-counter">
          Destroyed: {shapesDestroyed}
        </div>
      </div>
      
      {/* Enhanced debug indicators */}
      {isMouseInside && (
        <div 
          className="mouse-indicator"
          style={{
            position: 'absolute',
            left: `${mousePosition.x}%`,
            top: `${mousePosition.y}%`,
            width: '8px',
            height: '8px',
            backgroundColor: 'red',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 10,
            boxShadow: '0 0 10px red',
          }}
        />
      )}
    </div>
  );
};

export default AnimatedBackground;
